import { currencyMask } from '@utils/masks/currencyMask'
import { emotionTheme } from '@styles/theme'
import { TotalCell } from '../components/TotalCell'

import type { ReactNode } from 'react'
import type { ColumnAlignTypes, ColumnDefProps } from '../types'

export interface AlignedTotalValueProps {
  value: number | string | ReactNode
  /** Rótulo exibido acima do valor, dentro da mesma célula (ex.: `Total Bruto`). */
  label?: string
  currency?: boolean
}

export type AlignedTotalsValuesProps = Record<string, AlignedTotalValueProps>

export interface BuildAlignedTotalsRowProps {
  /** Colunas do corpo da tabela (`data[0]`) — a linha de total é espelhada a partir delas. */
  columns: ColumnDefProps[]
  /** Totais por `id` de coluna. Ids ausentes viram células vazias. */
  values: AlignedTotalsValuesProps
  /** Rótulo da linha, renderizado numa célula que agrupa as `labelSpan` primeiras colunas. */
  label?: string
  labelSpan?: number
  labelAlign?: ColumnAlignTypes
  background?: string
  fontWeight?: number
}

const TOTALS_BACKGROUND = emotionTheme.colors.gray.footer
const TOTALS_FONT_WEIGHT = 700
const TOTALS_LABEL_FONT_WEIGHT = 600

/**
 * Monta uma linha de totais que fica **exatamente** abaixo das colunas correspondentes.
 *
 * A linha é gerada espelhando as colunas do corpo da tabela (mesmo `id`, largura mínima/máxima,
 * alinhamento de header/corpo e coluna fixa), então cada total herda a largura, a posição
 * horizontal e o padding da sua coluna. Deve ser passada em `totalData` (renderiza dentro do
 * `<table>`, no mesmo contexto de scroll) — nunca em `fixedRow`, que fica fora da tabela e por
 * isso não acompanha as colunas nem o scroll horizontal.
 *
 * Totais com `label` usam [`TotalCell`](../components/TotalCell): rótulo em cima, valor em negrito
 * embaixo — o texto do rótulo não caberia inline em colunas estreitas como "Qtd. Total" (8rem).
 */
export const buildAlignedTotalsRow = ({
  columns,
  values,
  label,
  labelSpan = 1,
  labelAlign = 'left',
  background = TOTALS_BACKGROUND,
  fontWeight = TOTALS_FONT_WEIGHT
}: BuildAlignedTotalsRowProps): ColumnDefProps[] => {
  if (!columns.length) return []

  const totalCells: ColumnDefProps[] = columns.map((column) => {
    const total = values[column.id]

    const mirroredCell: ColumnDefProps = {
      id: column.id,
      columnName: column.columnName,
      value: total?.value ?? '',
      currency: !!total && (total.currency ?? column.currency),
      align: column.align,
      headerAlign: column.headerAlign,
      bodyAlign: column.bodyAlign,
      minWidth: column.minWidth,
      maxWidth: column.maxWidth,
      stickyPosition: column.stickyPosition,
      columnColor: background,
      bodyColumnColor: background,
      fontWeight,
      withoutTooltip: !total
    }

    if (!total?.label) return mirroredCell

    // Com rótulo empilhado a célula deixa de ser texto puro, então a máscara de moeda e a cor de
    // negativo passam a ser resolvidas aqui (o Table só as aplica em valores primitivos).
    const numericValue = Number(total.value)
    const isNumeric = total.value !== '' && total.value !== null && !Number.isNaN(numericValue)
    const isCurrency = total.currency ?? column.currency

    return {
      ...mirroredCell,
      value: (
        <TotalCell
          label={total.label}
          value={isCurrency && isNumeric ? currencyMask(numericValue.toFixed(2)) : total.value}
          align={column.bodyAlign || column.align}
          isNegative={isNumeric && numericValue < 0}
        />
      ),
      currency: false,
      withoutTooltip: true
    }
  })

  if (!label) return totalCells

  const span = Math.min(Math.max(labelSpan, 1), totalCells.length)

  const labelCell: ColumnDefProps = {
    ...totalCells[0],
    id: `${totalCells[0].id}-totals-label`,
    value: label,
    currency: false,
    align: labelAlign,
    headerAlign: labelAlign,
    bodyAlign: labelAlign,
    minWidth: 'auto',
    maxWidth: 'none',
    colSpan: span,
    fontWeight: TOTALS_LABEL_FONT_WEIGHT,
    withoutTooltip: true
  }

  return [labelCell, ...totalCells.slice(span)]
}

/** Índice da coluna `id`, usado para dimensionar o `labelSpan` sem hardcode de posição. */
export const getColumnIndex = (columns: ColumnDefProps[], id: string) => columns.findIndex((column) => column.id === id)
